export interface Group {
  id: string;
  name: string;
}

export interface ParsedGroups {
  groups: Group[];
  /** Lines that could not be turned into a group, with the reason. */
  skipped: Array<{ line: string; reason: string }>;
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Turns pasted text into groups, one per line. Accepted forms: `<id>`, `<name>,<id>`, `<id>,<name>` (comma, tab or
 * semicolon separated), or just a group name that is already known (configured or the admin's own groups).
 * Ids must be GUIDs, so a typo is reported instead of silently saved as a rule that never matches.
 */
export function parseGroupList(text: string, known: Group[]): ParsedGroups {
  const byId = new Map(known.map(g => [g.id.toLowerCase(), g]));
  const byName = new Map(known.map(g => [g.name.toLowerCase(), g]));
  const groups = new Map<string, Group>();
  const skipped: ParsedGroups['skipped'] = [];

  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const parts = line.split(/[,\t;]/).map(p => p.trim()).filter(Boolean);
    const id = parts.find(p => GUID.test(p));
    if (id) {
      const label = parts.find(p => p !== id);
      const name = label ?? byId.get(id.toLowerCase())?.name ?? id;
      groups.set(id.toLowerCase(), { id, name });
      continue;
    }
    const match = parts.length === 1 ? byName.get(parts[0].toLowerCase()) : undefined;
    if (match) {
      groups.set(match.id.toLowerCase(), match);
    } else if (parts.length === 1) {
      skipped.push({ line, reason: 'not a known group name; paste its id (a GUID) instead' });
    } else {
      skipped.push({ line, reason: 'no valid group id (GUID) found' });
    }
  }
  return { groups: [...groups.values()], skipped };
}
